/**
 * ActionMenu Component
 * A reusable kebab menu (⋮) dropdown for table row actions
 * Uses React Portal and Floating UI for proper positioning outside overflow contexts
 * Implements comprehensive accessibility with focus management
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, shift } from '@floating-ui/react';

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  /** Tailwind color class for the text (e.g., 'text-red-600') */
  colorClass?: string;
  /** Tailwind hover color class (e.g., 'hover:text-red-900') */
  hoverColorClass?: string;
  /** Dark mode color class (e.g., 'dark:text-red-400') */
  darkColorClass?: string;
  /** Dark mode hover color class (e.g., 'dark:hover:text-red-300') */
  darkHoverColorClass?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible label for the menu button */
  ariaLabel?: string;
}

export function ActionMenu({
  items,
  ariaLabel = 'Actions menu',
}: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  // Configure Floating UI for automatic positioning
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });

  // Ensure portal only renders on client side (SSR safety)
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        refs.floating.current &&
        !refs.floating.current.contains(event.target as Node) &&
        refs.domReference.current &&
        !refs.domReference.current.contains(event.target as Node)
      ) {
        // Just close the menu - don't force focus back to button.
        // Let natural browser focus behavior handle the clicked element.
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, refs]);

  // Focus management and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    // Transfer focus to first menu item when menu opens
    firstItemRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      // Handle Escape key
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        (refs.domReference.current as HTMLElement)?.focus();
        return;
      }

      // Handle Tab and Shift+Tab for focus trap
      if (event.key === 'Tab') {
        event.preventDefault();
        const menuItems =
          refs.floating.current?.querySelectorAll<HTMLButtonElement>(
            'button[role="menuitem"]'
          );
        if (!menuItems || menuItems.length === 0) return;

        const currentIndex = Array.from(menuItems).indexOf(
          document.activeElement as HTMLButtonElement
        );

        let nextIndex: number;
        if (event.shiftKey) {
          // Shift+Tab: Move to previous item (or wrap to last)
          nextIndex =
            currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1;
        } else {
          // Tab: Move to next item (or wrap to first)
          nextIndex =
            currentIndex >= menuItems.length - 1 ? 0 : currentIndex + 1;
        }

        menuItems[nextIndex].focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, refs]);

  const handleItemClick = (item: ActionMenuItem) => {
    item.onClick();
    setIsOpen(false);
    // Return focus to button after selection
    (refs.domReference.current as HTMLElement)?.focus();
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const dropdown = isOpen && isMounted && (
    <div
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        zIndex: 50,
      }}
      className="w-40 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      role="menu"
      aria-orientation="vertical"
    >
      <div className="py-1">
        {items.map((item, index) => {
          const colorClasses = [
            item.colorClass || 'text-gray-700',
            item.hoverColorClass || 'hover:text-gray-900',
            item.darkColorClass || 'dark:text-gray-300',
            item.darkHoverColorClass || 'dark:hover:text-gray-100',
          ].join(' ');

          return (
            <button
              key={index}
              ref={index === 0 ? firstItemRef : undefined}
              onClick={() => handleItemClick(item)}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${colorClasses}`}
              role="menuitem"
              tabIndex={0}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={refs.setReference}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={ariaLabel}
        className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
      >
        {/* Kebab icon (three vertical dots) */}
        <svg
          className="w-5 h-5"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
}
